import './App.css'
import {Box, Center, Flex, Spinner} from "@chakra-ui/react";
import {useColorMode, useColorModeValue} from "./components/ui/color-mode";
import Profiler from "./components/dev/Profiler.tsx";
import {Route, Routes, useLocation} from "react-router-dom";
import Navbar from "./components/Navbar.tsx";
import {useSideMenuVisibility} from "./hooks/useSideMenuVisiblity.ts";
import {useAppSelector} from "./hooks/store.ts";
import {isBarberLoggedIn} from "./slices/authSlice.ts";
import {useGetCurrentUserQuery} from "./services/userService.ts";
import {Suspense, useEffect, useMemo, useRef} from "react";
import {lazyWithRetry, clearChunkRetryFlag} from "./utils/lazyWithRetry.ts";
import {PrivateOutlet} from "./components/PrivateOutlet.tsx";
import {AdminOutlet} from "./components/AdminOutlet.tsx";
import CookieConsent from "./components/CookieConsent.tsx";
import {LimitGate} from "./components/Subscription/LimitGate.tsx";
import {SentryContextProvider} from "./components/SentryContextProvider.tsx";
import {BarberPageHeaderProvider} from "./contexts/BarberPageHeader.tsx";
import {analytics} from "./utils/analytics.ts";
import {onCLS, onINP, onLCP, onFCP, onTTFB, type Metric} from "web-vitals";
import {useVersionCheck} from "./hooks/useVersionCheck.ts";
import {StaleBuildBanner} from "./components/StaleBuildBanner.tsx";
import ImpersonationBanner from "./components/Admin/ImpersonationBanner.tsx";

// First-paint pages — eager (no Suspense flash on the most-visited routes).
import Home from "./pages/Home.tsx";
import Login from "./pages/Login.tsx";
import LoginSelection from "./pages/LoginSelection.tsx";
import RegisterSelection from "./pages/RegisterSelection.tsx";
import CustomerLogin from "./pages/customer/CustomerLogin.tsx";

// Code-split the rest. Vite emits one chunk per `lazy()` import and prefetches
// on hover via `<Link>`. Wraps `<Routes>` in `<Suspense>` below.
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard.tsx"))
const Booking = lazyWithRetry(() => import("./pages/Booking.tsx"))
const ReservationConfirmation = lazyWithRetry(() => import("./pages/ReservationConfirmation.tsx"))
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding.tsx"))
const BarberRegister = lazyWithRetry(() => import("./pages/BarberRegister.tsx"))
const CustomerRegister = lazyWithRetry(() => import("./pages/customer/CustomerRegister.tsx"))
const VerifyEmail = lazyWithRetry(() => import("./pages/customer/VerifyEmail.tsx"))
const InviteAccept = lazyWithRetry(() => import("./pages/InviteAccept.tsx"))
const BarberCalendar = lazyWithRetry(() => import("./pages/BarberCalendar.tsx"))
const BarberKiosk = lazyWithRetry(() => import("./pages/BarberKiosk.tsx"))
const BarberReservations = lazyWithRetry(() => import("./pages/BarberReservations.tsx"))
const BarberVacations = lazyWithRetry(() => import("./pages/BarberVacations.tsx"))
const BarberReviews = lazyWithRetry(() => import("./pages/BarberReviews.tsx"))
const BarberGiftCards = lazyWithRetry(() => import("./pages/BarberGiftCards.tsx"))
const GiftPurchase = lazyWithRetry(() => import("./pages/GiftPurchase.tsx"))
const BarberPlanOfDay = lazyWithRetry(() => import("./pages/BarberPlanOfDay.tsx"))
const BarberInsights = lazyWithRetry(() => import("./pages/BarberInsights.tsx"))
const BarberCustomers = lazyWithRetry(() => import("./pages/BarberCustomers.tsx"))
const BarberCustomer = lazyWithRetry(() => import("./pages/BarberCustomer.tsx"))
const BarberSettings = lazyWithRetry(() => import("./pages/BarberSettings.tsx"))
const BarberWaitlist = lazyWithRetry(() => import("./pages/BarberWaitlist.tsx"))
const BarberNotifications = lazyWithRetry(() => import("./pages/BarberNotifications.tsx"))
// HIDDEN_TODO #27 — page depends on GET customer_reservation/reservations, removed by BE (see docs/HIDDEN_TODO.md)
// const CustomerReservations = lazyWithRetry(() => import("./pages/CustomerReservations.tsx"))
const CustomerWaitlist = lazyWithRetry(() => import("./pages/customer/CustomerWaitlist.tsx"))
const WaitlistClaim = lazyWithRetry(() => import("./pages/customer/WaitlistClaim.tsx"))
const CustomerNotifications = lazyWithRetry(() => import("./pages/customer/CustomerNotifications.tsx"))
const CustomerPortal = lazyWithRetry(() => import("./pages/customer/CustomerPortal.tsx"))
const AdminPlatformLayout = lazyWithRetry(() => import("./pages/admin/AdminPlatformLayout.tsx"))
const AdminIndex = lazyWithRetry(() => import("./pages/admin/AdminIndex.tsx"))
const AdminSendSms = lazyWithRetry(() => import("./pages/admin/AdminSendSms.tsx"))
const AdminSmsConfig = lazyWithRetry(() => import("./pages/admin/AdminSmsConfig.tsx"))
const AdminAiCost = lazyWithRetry(() => import("./pages/admin/AdminAiCost.tsx"))
const AdminSendEmail = lazyWithRetry(() => import("./pages/admin/AdminSendEmail.tsx"))
const AdminEmailBuilder = lazyWithRetry(() => import("./pages/admin/AdminEmailBuilder.tsx"))
const AdminEmailTemplates = lazyWithRetry(() => import("./pages/admin/AdminEmailTemplates.tsx"))
const AdminBlog = lazyWithRetry(() => import("./pages/admin/AdminBlog.tsx"))
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard.tsx"))
const AdminBarberShops = lazyWithRetry(() => import("./pages/admin/AdminBarberShops.tsx"))
const AdminBarbers = lazyWithRetry(() => import("./pages/admin/AdminBarbers.tsx"))
const AdminCustomers = lazyWithRetry(() => import("./pages/admin/AdminCustomers.tsx"))
const Explore = lazyWithRetry(() => import("./pages/Explore.tsx"))
const RateVisit = lazyWithRetry(() => import("./pages/RateVisit.tsx"))
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe.tsx"))
const Pricing = lazyWithRetry(() => import("./pages/Pricing.tsx"))
const Privacy = lazyWithRetry(() => import("./pages/Privacy.tsx"))
const Terms = lazyWithRetry(() => import("./pages/Terms.tsx"))
const CookiePolicy = lazyWithRetry(() => import("./pages/CookiePolicy.tsx"))
const About = lazyWithRetry(() => import("./pages/About.tsx"))
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword.tsx"))
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword.tsx"))
const NotFound = lazyWithRetry(() => import("./pages/NotFound.tsx"))

const RouteFallback = () => (
    <Center minH="60vh"><Spinner size="xl" /></Center>
)

export default function App() {
    const isDevelopment = (!process.env.NODE_ENV || process.env.NODE_ENV === 'development')
    const isLogged = useAppSelector(isBarberLoggedIn);
    const {data: user} = useGetCurrentUserQuery(undefined, {skip: !isLogged})
    const {showSideMenu} = useSideMenuVisibility(user);
    const {colorMode, setColorMode} = useColorMode()
    const location = useLocation();
    const savedModeRef = useRef<string | null>(null);

    // Auth surfaces (Login/Register/CustomerLogin/CustomerRegister/barber + customer
    // variants) ship a full-bleed AuthShell with their own brand panel; the public
    // top bar would stack above and push minH=100vh into a forced scrollbar.
    const isAuthFullBleed = useMemo(() => {
        const p = location.pathname
        return p === '/login' || p === '/register' || p === '/onboarding'
            || p.startsWith('/login/') || p.startsWith('/register/')
            || p.startsWith('/onboarding/') || p === '/invite/accept'
            || p === '/customer/verify-email'
            // Platform admin ships its own left rail + header (see
            // AdminPlatformLayout). The public Navbar would stack above and
            // double-decorate the page.
            || p === '/admin' || p.startsWith('/admin/')
            // Unsubscribe is a public standalone page (clicked from an
            // email/SMS footer) — ships its own minimal wordmark header +
            // mono footer per the Barbermate-11 design.
            || p === '/unsubscribe'
    }, [location.pathname])

    // Determine forced color mode per route.
    // /r/ (public booking page) is intentionally NOT forced here — the booking
    // surface scopes its own dark/light via Chakra <DarkMode> based on the
    // shop's `theme` config, so the navbar follows the user's preference
    // while only the booking card itself flips.
    const forcedMode = useMemo(() => {
        if (location.pathname === '/' || [
            '/reservation-confirmation/', '/rate/',
            '/explore', '/pricing', '/login', '/register',
            '/privacy', '/terms', '/cookies', '/about',
            '/barber/', '/dashboard', '/admin', '/onboarding',
            '/customer/verify-email',
        ].some(r => location.pathname.startsWith(r))) return 'light'
        return null
    }, [location.pathname])

    useEffect(() => {
        if (forcedMode) {
            // Save user's preference before forcing (only once per forced section)
            if (savedModeRef.current === null) {
                savedModeRef.current = colorMode
            }
            setColorMode(forcedMode as 'light' | 'dark')
        } else if (savedModeRef.current !== null) {
            // Leaving a forced route — restore user's preference
            setColorMode(savedModeRef.current as 'light' | 'dark')
            savedModeRef.current = null
        }
    }, [forcedMode]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        analytics.pageview(location.pathname);
    }, [location.pathname]);

    // Set GA4 user_id + user_properties for logged-in barbers
    useEffect(() => {
        if (user?.id) {
            analytics.setUser(String(user.id), 'barber', {
                plan_type: user.barber_shop?.account_type,
            });
        }
    }, [user?.id]);

    useEffect(() => {
        const report = (metric: Metric) => {
            analytics.webVital(metric.name, metric.value, metric.rating, metric.id);
        };
        onCLS(report);
        onINP(report);
        onLCP(report);
        onFCP(report);
        onTTFB(report);
    }, []);

    useVersionCheck();

    // After a stale-chunk reload, clear the retry guard once the app has
    // rendered cleanly for a few seconds. If a fresh failure happens before
    // that, we'll let the error propagate instead of looping reloads.
    useEffect(() => {
        const id = window.setTimeout(clearChunkRetryFlag, 5000);
        return () => window.clearTimeout(id);
    }, []);

    return (
        <SentryContextProvider>
            <BarberPageHeaderProvider>
            {/* overflowX=hidden makes this Box the app's scroll container
                (overflow-y computes to auto) — --bm-consent-h reserves room
                for the fixed cookie banner so bottom controls stay tappable. */}
            <Box minHeight="100vh" bg={useColorModeValue('gray.50', 'gray.900')} overflowX="hidden" paddingBottom="var(--bm-consent-h, 0px)">
                {!isAuthFullBleed && <Navbar/>}
                <ImpersonationBanner/>
                <StaleBuildBanner/>
                <Flex direction={"column"} ml={{base: 0, md: showSideMenu ? 60 : 0}}
                      position={"relative"}>
                    {isDevelopment ? <Profiler/> : null}
                    <Suspense fallback={<RouteFallback />}>
                    <Routes>
                        <Route path="" element={<Home/>}/>
                        <Route path="/explore" element={<Explore/>}/>
                        <Route path="/pricing" element={<Pricing/>}/>
                        <Route path="/unsubscribe" element={<Unsubscribe/>}/>
                        <Route path="/privacy" element={<Privacy/>}/>
                        <Route path="/terms" element={<Terms/>}/>
                        <Route path="/cookies" element={<CookiePolicy/>}/>
                        <Route path="/about" element={<About/>}/>
                        <Route path="/gift" element={<GiftPurchase/>}/>
                        <Route path="/forgot-password" element={<ForgotPassword/>}/>
                        <Route path="/reset-password" element={<ResetPassword/>}/>
                        <Route path="dashboard" element={<PrivateOutlet/>}>
                            <Route index element={<Dashboard/>}/>
                        </Route>
                        <Route path="onboarding" element={<PrivateOutlet/>}>
                            <Route index element={<Onboarding/>}/>
                        </Route>
                        <Route path="login" element={<LoginSelection/>}/>
                        <Route path="login/barber" element={<Login/>}/>
                        <Route path="register" element={<RegisterSelection/>}/>
                        <Route path="register/barber" element={<BarberRegister/>}/>
                        <Route path="invite/accept" element={<InviteAccept/>}/>
                        <Route path="barber/calendar" element={<PrivateOutlet/>}>
                            <Route index element={<BarberCalendar/>}/>
                        </Route>
                        <Route path="barber/kiosk" element={<PrivateOutlet/>}>
                            <Route index element={<BarberKiosk/>}/>
                        </Route>
                        <Route path="barber/reservations" element={<PrivateOutlet/>}>
                            <Route index element={<BarberReservations/>}/>
                        </Route>
                        <Route path="barber/vacations" element={<PrivateOutlet/>}>
                            <Route index element={<BarberVacations/>}/>
                        </Route>
                        <Route path="barber/reviews" element={<PrivateOutlet/>}>
                            <Route index element={<BarberReviews/>}/>
                        </Route>
                        <Route path="barber/gift-cards" element={<PrivateOutlet/>}>
                            <Route index element={<BarberGiftCards/>}/>
                        </Route>
                        <Route path="barber/settings" element={<PrivateOutlet/>}>
                            <Route index element={<BarberSettings/>}/>
                        </Route>
                        <Route path="barber/waitlist" element={<PrivateOutlet/>}>
                            <Route index element={<BarberWaitlist/>}/>
                        </Route>
                        <Route path="barber/notifications" element={<PrivateOutlet/>}>
                            <Route index element={<BarberNotifications/>}/>
                        </Route>
                        <Route path="barber/plan-of-day" element={<PrivateOutlet/>}>
                            <Route index element={<BarberPlanOfDay/>}/>
                        </Route>
                        {/* HIDDEN_TODO #22 — Insights page uses mock data only. Direct URL still resolves so the design can be reviewed; sidebar entry kept commented in Navbar. */}
                        <Route path="barber/insights" element={<PrivateOutlet/>}>
                            <Route index element={<BarberInsights/>}/>
                        </Route>
                        <Route path="barber/customers" element={<PrivateOutlet/>}>
                            <Route index element={<BarberCustomers/>}/>
                            <Route path=":id" element={<BarberCustomer/>}/>
                        </Route>
                        <Route path="admin" element={<AdminOutlet/>}>
                            <Route element={<AdminPlatformLayout/>}>
                                <Route index element={<AdminIndex/>}/>
                                <Route path="dashboard" element={<AdminDashboard/>}/>
                                <Route path="barber-shops" element={<AdminBarberShops/>}/>
                                <Route path="barbers" element={<AdminBarbers/>}/>
                                <Route path="customers" element={<AdminCustomers/>}/>
                                <Route path="ai-cost" element={<AdminAiCost/>}/>
                                <Route path="sms-config" element={<AdminSmsConfig/>}/>
                                <Route path="sms" element={<AdminSendSms/>}/>
                                <Route path="email" element={<AdminSendEmail/>}/>
                                <Route path="email-builder" element={<AdminEmailBuilder/>}/>
                                <Route path="email-templates" element={<AdminEmailTemplates/>}/>
                                <Route path="blog" element={<AdminBlog/>}/>
                            </Route>
                        </Route>
                        {/* Customer Account Routes */}
                        <Route path="login/customer" element={<CustomerLogin/>}/>
                        <Route path="register/customer" element={<CustomerRegister/>}/>
                        <Route path="customer/verify-email" element={<VerifyEmail/>}/>
                        <Route path="customer/portal" element={<CustomerPortal/>}/>
                        <Route path="customer/waitlist" element={<CustomerWaitlist/>}/>
                        <Route path="customer/waitlist/:id/claim" element={<WaitlistClaim/>}/>
                        <Route path="customer/notifications" element={<CustomerNotifications/>}/>
                        {/* HIDDEN_TODO #27 — legacy customer-reservations page; its BE endpoint
                            (GET customer_reservation/reservations by bare email) was removed as a
                            privacy hole. Replacement is the OTP-gated lookup on /r/:barberSlug. */}
                        {/* <Route path="customer/reservations/:barberShopId" element={<CustomerReservations/>}/> */}
                        <Route path="r/:barberSlug" element={<Booking/>}/>
                        <Route path="rate/:token" element={<RateVisit/>}/>
                        <Route path="reservation-confirmation/:reservationId" element={<ReservationConfirmation/>}/>
                        <Route path="*" element={<NotFound/>}/>
                    </Routes>
                    </Suspense>
                </Flex>
                <CookieConsent/>
                <LimitGate/>
            </Box>
            </BarberPageHeaderProvider>
        </SentryContextProvider>
    )
}
